/*
 * SSE client for POST /api/ai/chat.
 *
 * axios is used everywhere else in the app but cannot stream a response body in
 * the browser, so this one endpoint goes through fetch. It reuses the same base
 * URL and the same localStorage token as api/axios.js.
 */

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

/**
 * Streams one chat turn.
 *
 * @param body    { messages, approvals? } — messages is the full transcript
 * @param onEvent (type, data) for each server event
 * @param signal  AbortSignal to cancel the turn
 */
export default async function streamChat(body, onEvent, signal) {
  const token = localStorage.getItem('token');
  const res = await fetch(`${BASE}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
    signal,
  });

  // Failures before the stream opens are ordinary JSON responses (503 when the
  // deployment has no key, 429 when the daily cap is hit).
  if (!res.ok || !res.body) {
    let message = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch {
      /* non-JSON body; keep the status message */
    }
    onEvent('error', { message });
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Frames are separated by a blank line; the last piece may be partial.
    const frames = buffer.split('\n\n');
    buffer = frames.pop();

    for (const frame of frames) {
      // ": keep-alive" comment frames hold the connection open through
      // proxies and carry no event.
      if (!frame.trim() || frame.startsWith(':')) continue;
      const type = /^event: (.+)$/m.exec(frame)?.[1];
      const raw = /^data: (.+)$/m.exec(frame)?.[1];
      if (!type || !raw) continue;
      try {
        onEvent(type, JSON.parse(raw));
      } catch {
        /* ignore an unparsable frame rather than killing the stream */
      }
    }
  }
}
